<template>
  <br />
  <div class="col-sm-12">
    <!-- Tabla de presentación -->  
    <div class="card">

      <div class="card-header card-header-compact">
        <h3 class="card-header-title">{{ tableTitle }}</h3>
        <div class="card-header-toolbar">
          <small v-if="loading">Cargando datos...</small>
          <input
            v-model="searchQuery"
            @keyup.enter="onSearch"
            class="form-control form-control-sm"
            placeholder="Buscar por número de póliza, contratante o RFC..."
            style="width: 320px"
          />
          <button type="button" class="btn btn-sm btn-primary" :disabled="loading" @click="onSearch">Buscar</button>
        </div>
      </div>

      <div class="card-body">
        <DataTable
          :rows="tableData"
          :columns="displayColumns"
          :loading="loading"
          :error="error"
          @view="onView"
          @edit="onEdit"
          @delete="onDelete"
        />
        <div v-if="error" class="mt-2">
          <button type="button" class="btn btn-sm btn-outline-primary" @click="retry">Reintentar</button>
        </div>
        <p v-if="!loading && !error && tableData.length === 0" class="text-muted mb-0">{{ emptyMessage }}</p>
      </div>

      <div class="card-footer">
        <div class="d-flex justify-content-between align-items-center mt-2">
          <div>
            <button class="btn btn-sm btn-secondary me-2" :disabled="!hasPrevious || loading"
              @click="prevPage">Anterior</button>
              <button class="btn btn-sm btn-secondary" :disabled="!hasNext || loading"
              @click="nextPage">Siguiente</button>
          </div>
          <div>
            <small>Total de registros: {{ formatNumber(totalItems) }}</small>
            <small v-if="!loading && tableData.length > 0"> Mostrando {{ tableData.length }} de {{
              formatNumber(totalItems)
              }}</small>
          </div>
          <div>
            <small>Página {{ currentPage }} de {{ formatNumber(totalPages) }}</small>
          </div>
        </div>
      </div>

    </div>
    
  </div>

</template>

<script>
import api from '@/axios';
import DataTable from '@/components/DataTablePolizas.vue';
import { useSeguroStore } from '@/stores/seguroStore';

const STALE = Symbol('stale');

export default {
  setup() {
        const store = useSeguroStore();
        return { store };
  },
  name: 'polizas',
  components: { DataTable },
  props: {
    tableName: {
      type: String,
      default: 'polizas',
    },
    tableTitle: {
      type: String,
      default: 'Pólizas',
    },
    // Etiquetas personalizadas para columnas (clave es el nombre de la propiedad, valor es la etiqueta a mostrar)
    labels: {
      type: Object,
      default: () => ({'Id': '#', 'poliza': 'Número de Póliza', 'contratante': 'Contratante', 'rfc': 'RFC', 'producto': 'Producto', 'aseguradora': 'Aseguradora', 'status': 'Estado'}),
    },
    // Opcional: columnas a mostrar (si no se proporciona, se muestran todas)
    showColumns: {
      type: Array,
      default: () => ['Id', 'poliza', 'contratante', 'rfc', 'producto', 'aseguradora', 'status'],
    },
  },  
  data() {
    return {
      tableColumns: [],
      tableData: [],
      loading: false,
      error: null,
      searchQuery: '',
      // Texto de la última búsqueda confirmada: es el que se envía al paginar,
      // aunque el usuario haya editado la caja sin confirmar.
      appliedSearch: '',
      requestId: 0,
      abortController: null,
      lastPage: 1,
      pageSize: 20,
      currentPage: 1,
      totalItems: 0,
      totalPages: 1,
      hasPrevious: false,
      hasNext: false,
    };
  },

  computed: {
    emptyMessage() {
      return this.appliedSearch
        ? 'No se encontraron pólizas para la búsqueda'
        : 'No hay registros';
    },
    displayColumns() {
      // Filter and order by showColumns, applying friendly labels
      const available = new Set(this.tableColumns.map(c => c.value));
      return this.showColumns
        .filter(key => available.has(key))
        .map(key => ({ text: this.friendlyLabel(key), value: key }));
    },
  },

  watch: {
    // El componente se reutiliza si la ruta cambia sólo de query (por ejemplo,
    // al entrar desde Contratantes estando ya en Pólizas Emitidas).
    '$route.query.search'(value) {
      const term = this.normalizeSearch(value);
      if (term === this.appliedSearch) return;
      this.searchQuery = term;
      this.appliedSearch = term;
      this.loadData(1);
    },
  },

  created() {
    // Criterio inicial desde la URL (?search=): ver
    // specs/003-contratante-ver-polizas/contracts/polizas-emitidas-url.md
    const initial = this.normalizeSearch(this.$route?.query?.search);
    this.searchQuery = initial;
    this.appliedSearch = initial;
    this.loadData();
  },

  methods: {
    // Devuelve el payload, null si hubo error, o STALE si otra petición más
    // reciente reemplazó a ésta (su resultado no debe mostrarse).
    async fetchData(page) {
      const id = ++this.requestId;
      if (this.abortController) this.abortController.abort();
      const controller = new AbortController();
      this.abortController = controller;
      try {
        this.loading = true;
        this.error = null;
        const response = await api.get(this.tableName, {
          params: { page, pageSize: this.pageSize, search: this.appliedSearch || undefined },
          signal: controller.signal,
        });
        if (id !== this.requestId) return STALE;
        const payload = response.data;
        if (!payload || !Array.isArray(payload.items)) {
          console.warn('Respuesta inesperada de la API (se esperaba { items, page, pageSize, totalItems, totalPages, hasPrevious, hasNext }):', payload);
          this.error = 'La API devolvió un formato inesperado. Revisá la consola para más detalle.';
          return null;
        }
        return payload;
      } catch (err) {
        if (id !== this.requestId) return STALE;
        console.error('Error fetching data:', err);
        this.error = 'No se pudieron cargar los datos.';
        return null;
      } finally {
        if (id === this.requestId) this.loading = false;
      }
    },

    getColumns(data) {
      if (!Array.isArray(data) || data.length === 0) return [];
      return Object.keys(data[0]);
    },

    buildTableColumns(data) {
      const columns = this.getColumns(data);
      // Prepend the row-number column explicitly
      return [{ text: '#', value: 'Id' }, ...columns.map(col => ({ text: this.friendlyLabel(col), value: col }))];
    },

    // El "Id" de fila es la posición dentro del listado completo (no el id real
    // del registro, que sigue viajando en `item.id` para las acciones), calculada
    // a partir de la página actual ya que el backend pagina server-side.
    buildTableData(items, page) {
      const offset = (page - 1) * this.pageSize;
      return items.map((item, index) => ({ Id: offset + index + 1, ...item }));
    },

    async loadData(page = this.currentPage) {
      this.lastPage = page;
      const result = await this.fetchData(page);
      if (result === STALE) return;
      if (!result) {
        this.tableData = [];
        return;
      }
      this.currentPage = result.page ?? page;
      this.totalItems = result.totalItems ?? 0;
      this.totalPages = result.totalPages ?? 1;
      this.hasPrevious = result.hasPrevious ?? false;
      this.hasNext = result.hasNext ?? false;
      this.tableColumns = this.buildTableColumns(result.items);
      this.tableData = this.buildTableData(result.items, this.currentPage);
    },

    friendlyLabel(key) {
      // Prefer labels prop mappings (raw key or generated base), fallback to generated label
        if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, key)) return this.labels[key];
        const base = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, base)) return this.labels[base];
        return base;
    },

    prevPage() {
      if (this.hasPrevious) this.loadData(this.currentPage - 1);
    },

    nextPage() {
      if (this.hasNext) this.loadData(this.currentPage + 1);
    },

    formatNumber(val) {
      return (Number(val) || 0).toLocaleString('en-US');
    },

    normalizeSearch(value) {
      const raw = Array.isArray(value) ? value[0] : value;
      return typeof raw === 'string' ? raw.trim() : '';
    },

    onSearch() {
      this.appliedSearch = this.searchQuery.trim();
      // Mantiene la URL en sync para que recargar conserve el criterio.
      this.$router?.replace({ query: this.appliedSearch ? { search: this.appliedSearch } : {} });
      this.loadData(1);
    },

    retry() {
      this.loadData(this.lastPage);
    },

    onEdit(item) {
      // Abre el detalle del seguro con el id del registro seleccionado      
      this.store.folio = item.id;
      this.$router.push({ name: 'detallePoliza', params: { id: item.id }, query: { tableName: this.tableName } });
    },

    onView(item) {
      this.$router.push({ name: 'verPoliza', params: { id: item.id } });
    },

    onAccount(item) {
      // Estado de cuenta — por implementar
    },

    onDelete(item) {
      // confirmación simple antes de eliminar (no realiza eliminación real aquí)
      if (confirm(`¿Eliminar el folio '${item.nombre || item.folio}'?`)) {
        // Aquí deberías llamar a la API para eliminar. Por ahora solo registramos en consola.
        try {
          // Llamada a la API para eliminar el registro
          api.delete(`polizas/${item.folio}`);
        } catch (err) {
          console.error('Error deleting item:', err);
          this.error = 'No se pudo eliminar el registro.';
          return;
        }
        this.tableData = this.tableData.filter(i => i.folio !== item.folio);
      }
    },

  },
};

</script>

<style scoped>
/* Center feather SVG icons inside small action buttons */
.icon-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.icon-btn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}
</style>
